import * as React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Plus as PlusIcon } from '@phosphor-icons/react';

import { toast } from '@/components/core/v2/toaster';

import { getAmenities } from '@/api/amenities';
import { getBlockedSlots } from '@/api/reservations';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';

import { NewReservationDialog } from './new-reservation-dialog';
import { ReservationDetailsDialog } from './reservation-details-dialog';

export function ReservationsCalendar(): React.JSX.Element {
  const { community, user } = useUser();
  const dialog = useDialog();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const calendarRef = React.useRef<FullCalendar>(null);
  const [selectedDate, setSelectedDate] = React.useState<{ start: Date; end: Date } | null>(null);
  const [showNewReservationDialog, setShowNewReservationDialog] = React.useState(false);
  const [selectedReservation, setSelectedReservation] = React.useState<any>(null);
  const [showReservationDetailsDialog, setShowReservationDetailsDialog] = React.useState(false);

  // Fetch amenities
  const { data: amenitiesData, isLoading: isAmenitiesLoading } = useQuery({
    queryKey: ['amenities', community?.id],
    queryFn: () => getAmenities(community?.id || ''),
    enabled: !!community?.id,
  });

  const amenities = amenitiesData?.amenities?.filter((a) => a.is_active) || [];

  // Check if user is a manager/admin
  const isManager = React.useMemo(() => {
    if (!user || !community) return false;

    // Check account-level roles
    const accountRoles = user.account_roles || [];
    const hasAccountRole = accountRoles.some(role =>
      ['account_owner', 'account_admin', 'account_manager'].includes(role.name)
    );

    // Check community-level roles
    const communityRoles = community.roles || [];
    const hasCommunityRole = communityRoles.includes('community_manager');

    return hasAccountRole || hasCommunityRole;
  }, [user, community]);

  const handleEventClick = (clickInfo: any) => {
    if (!isManager) return;

    // Extract reservation data from the event
    const eventId = clickInfo.event.id;
    const parts = eventId.split('-');
    if (parts[0] === 'blocked') {
      const reservationId = parts[1];

      // Find the reservation data
      const reservation = blockedSlotsData?.find(slot => slot.id === reservationId);
      if (reservation) {
        setSelectedReservation(reservation);
        setShowReservationDetailsDialog(true);
      }
    }
  };


  // Fetch blocked slots for all amenities
  const {
    data: blockedSlotsData,
    isLoading: isBlockedSlotsLoading,
  } = useQuery({
    queryKey: ['blocked-slots-all', community?.id],
    queryFn: async () => {
      if (!community?.id || !amenities.length) return [];

      const start = dayjs().startOf('month').subtract(1, 'month').format('YYYY-MM-DD');
      const end = dayjs().endOf('month').add(2, 'months').format('YYYY-MM-DD');

      // Fetch blocked slots for all amenities
      const allBlockedSlots = [];
      for (const amenity of amenities) {
        try {
          const response = await getBlockedSlots(amenity.id, start, end);
          const slots = response.blocked_slots || [];

          // Add amenity name to each slot
          const slotsWithAmenity = slots.map(slot => ({
            ...slot,
            amenity_name: amenity.name,
            amenity_id: amenity.id,
          }));

          allBlockedSlots.push(...slotsWithAmenity);
        } catch (error) {
          // Skip amenities that fail to load
          console.warn(`Failed to load blocked slots for amenity ${amenity.id}:`, error);
        }
      }

      return allBlockedSlots;
    },
    enabled: Boolean(community?.id) && amenities.length > 0,
  });

  // Convert blocked slots to calendar events
  const events = React.useMemo(() => {
    if (!blockedSlotsData) return [];

    return blockedSlotsData.map((slot, index) => {
      const isApproved = slot.status === 'approved';
      const isPending = slot.status === 'pending';
      const isMine = slot.is_mine;
      const amenityName = slot.amenity_name || 'Unknown Amenity';

      let color = theme.palette.primary.main; // Use theme primary color
      let title = `${amenityName}`;

      if (isMine) {
        // User's own reservation
        if (isPending) {
          color = theme.palette.warning.main;
          title += ' - Your Reservation (Pending)';
        } else {
          title += ' - Your Reservation';
        }
      } else if (isManager && slot.user) {
        // Manager viewing someone else's reservation
        const userName = `${slot.user.first_name} ${slot.user.last_name}`;
        const unitName = slot.unit?.name || 'Unknown Unit';

        if (isPending) {
          color = theme.palette.info.main;
          title += ` - ${userName} (${unitName}) - Pending`;
        } else {
          title += ` - ${userName} (${unitName})`;
        }
      } else {
        // Regular user viewing other reservations
        if (isPending) {
          color = theme.palette.info.main;
          title += ' - Pending Approval';
        } else {
          title += ' - Reserved';
        }
      }

      return {
        id: `blocked-${slot.id}`,
        title,
        start: dayjs.utc(slot.start_time).local().toISOString(),
        end: dayjs.utc(slot.end_time).local().toISOString(),
        display: 'block',
        color,
      };
    });
  }, [blockedSlotsData, theme, isManager]);




  if (isAmenitiesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!amenities.length) {
    return (
      <Card sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Amenities Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your community has not enabled amenity reservations yet.
        </Typography>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              Reserve an Amenity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {community?.amenity_reservations_description?.trim() ||
                'View all amenity reservations. Approved reservations appear in blue; your pending requests remain visible to you in orange while waiting for approval. Use the "New Reservation" button to book an amenity.'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PlusIcon size={18} />}
            onClick={() => setShowNewReservationDialog(true)}
            sx={{ minWidth: 'fit-content' }}
          >
            New Reservation
          </Button>
        </Box>


        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
            label="Approved"
            size="small"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: 600
            }}
          />
          <Chip
            label="Pending (You)"
            size="small"
            sx={{
              bgcolor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
              fontWeight: 600
            }}
          />
        </Stack>

        {isBlockedSlotsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ '& .fc': { fontSize: '0.875rem' } }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              initialView="timeGridWeek"
              editable={false}
              dayMaxEvents
              weekends
              events={events}
              eventClick={handleEventClick}
              height="auto"
            />
          </Box>
        )}

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>How it works:</strong> Select your amenity, then drag on the calendar to choose a time.
            Submit the request and a community manager will review it if approval is required.
          </Typography>
        </Box>
      </Card>

      <NewReservationDialog
        open={showNewReservationDialog}
        onClose={() => setShowNewReservationDialog(false)}
        onSuccess={() => {
          setShowNewReservationDialog(false);
          // Refresh calendar data
          queryClient.invalidateQueries({ queryKey: ['blocked-slots-all', community?.id] });
        }}
      />

      <ReservationDetailsDialog
        open={showReservationDetailsDialog}
        reservation={selectedReservation}
        onClose={() => {
          setShowReservationDetailsDialog(false);
          setSelectedReservation(null);
        }}
        onSuccess={() => {
          setShowReservationDetailsDialog(false);
          setSelectedReservation(null);
          // Refresh calendar data
          queryClient.invalidateQueries({ queryKey: ['blocked-slots-all', community?.id] });
        }}
      />
    </>
  );
}
